import React, { useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { AppBar, Toolbar, Button, MenuList, MenuListItem, Separator, Handle, Frame, Hourglass } from 'react95';
import windowsIcon from '../../assets/windows95.png';
import homeIcon from '../../assets/homepage.png';
import refreshIcon from '../../assets/refresh.png';
import calendarIcon from '../../assets/calendar.png';
import { logout } from '../../services/authService';
import './QDeckToolBar.scss';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { userSelector } from '../../selectors/authSelectors';
import { isCurrentUserAdmin } from '../../utils/taskUtils';
import { useOutsideClick } from '../../utils/componentUtils';
import { useGetTaskDetailAvailableTaxReturnsQuery, useGetTaskQuery } from '../../api/opsSystemApi';
import { useFetchQDeckUsersQuery } from '../../api/qdeckApi';

const QDeckToolBar = ({ type, menuItems, tabs, searchItem, user, logout }) => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const menuRef = useRef(null);
  useOutsideClick(menuRef, () => setMenuOpen(false));

  const { data: task = {}, isLoading: isTaskLoading } = useGetTaskQuery(taskId, {
    skip: !taskId
  });
  const { data: qdeckUsers = [], isLoading: areQDeckUsersLoading } = useFetchQDeckUsersQuery();

  const isAdmin = isCurrentUserAdmin(user.email, qdeckUsers);

  const {
    data: availableTaxReturns,
    isLoading: areAvailableTaxReturnsLoading
  } = useGetTaskDetailAvailableTaxReturnsQuery(task.user_id, {
    skip: !task?.user_id
  });

  const year = useMemo(() => {
    if (task) {
      return task.year;
    }
    return null;
  }, [task]);

  const availableTaxYears = useMemo(() => {
    if (availableTaxReturns) {
      return availableTaxReturns.map((taxReturn) => taxReturn.year).sort((a, b) => b - a);
    }
    return [];
  }, [availableTaxReturns]);

  if (areAvailableTaxReturnsLoading || areQDeckUsersLoading) {
    return null;
  }

  const onTaxYearSelect = async (currentYear, selectedYear) => {
    if (currentYear !== selectedYear) {
      const taskId = availableTaxReturns.find((taxReturn) => taxReturn.year === selectedYear).id;
      navigate(`/task-detail/${taskId}`);
    }
  };
  const taxYearItems = availableTaxYears.map((taxYear) => ({
    name: taxYear,
    primary: year === taxYear,
    onClick: () => onTaxYearSelect(year, taxYear)
  }));

  return (
    <AppBar>
      <Toolbar className='toolbar'>
        <div className='toolbar-left'>
          <div className='toolbar-menu' ref={menuRef}>
            {type === 'task detail' ? (
              <div className='toolbar-menu-expanded'>
                <Button onClick={menuItems[0]?.onClick} size='lg' className='toolbar-menu-btn'>
                  <img src={homeIcon} alt='home' height={24} />
                  {menuItems[0]?.name}
                </Button>
                <Handle size={36} />
                <Frame variant='status'>
                  <div style={{ display: 'block', width: 'auto' }}>
                    <Button
                      onClick={() => setMenuOpen(!menuOpen)}
                      active={menuOpen}
                      variant='thin'
                      disabled={isTaskLoading}
                      className='toolbar-menu-btn'>
                      {menuItems[1]?.name}
                      {isTaskLoading ? (
                        <Hourglass size={24} />
                      ) : (
                        <img src={calendarIcon} alt='select year' height={24} />
                      )}
                    </Button>
                    {menuOpen && (
                      <MenuList onClick={() => setMenuOpen(false)} className='toolbar-menu-list'>
                        {taxYearItems &&
                          taxYearItems.map((item) => (
                            <MenuListItem key={item.name} primary={item.primary} onClick={item.onClick}>
                              {item.name}
                            </MenuListItem>
                          ))}
                      </MenuList>
                    )}
                  </div>
                </Frame>
                <Frame variant='status'>
                  <Button
                    onClick={menuItems[2]?.onClick}
                    variant='thin'
                    disabled={isTaskLoading}
                    className='toolbar-menu-btn'>
                    {menuItems[2]?.name}
                    {isTaskLoading ? <Hourglass size={24} /> : <img src={refreshIcon} alt='refresh' height={24} />}
                  </Button>
                </Frame>
                <Frame variant='status'>
                  <Button
                    onClick={menuItems[3]?.onClick}
                    variant='thin'
                    disabled={isTaskLoading || !task.user}
                    className='toolbar-menu-btn'>
                    {menuItems[3]?.name}
                    {isTaskLoading && <Hourglass size={24} />}
                  </Button>
                </Frame>
                <Handle size={36} />
              </div>
            ) : (
              <>
                <Button onClick={() => setMenuOpen(!menuOpen)} active={menuOpen} size='lg' className='toolbar-menu-btn'>
                  <img src={windowsIcon} alt='windows95' height={24} />
                  Settings
                </Button>
                {menuOpen && (
                  <MenuList onClick={() => setMenuOpen(false)}>
                    {menuItems &&
                      menuItems.map((item) => (
                        <MenuListItem key={item.name} primary={item.primary} onClick={item.onClick}>
                          {item.name}
                        </MenuListItem>
                      ))}
                    {isAdmin && (
                      <>
                        <Separator />
                        <Link to='/manage-users'>
                          <MenuListItem>Manage Users</MenuListItem>
                        </Link>
                      </>
                    )}
                    <Separator />
                    <MenuListItem onClick={logout}>Log out</MenuListItem>
                  </MenuList>
                )}
              </>
            )}
          </div>

          {tabs && (
            <div className='toolbar-tabs'>
              {tabs.map((tab, i) => (
                <Button
                  key={i}
                  variant='raised'
                  primary
                  active={tab.active}
                  size={tab.active ? 'lg' : 'md'}
                  className={tab.active ? 'selected' : null}
                  onClick={tab.onClick}>
                  {tab.name}
                </Button>
              ))}
            </div>
          )}
        </div>

        {searchItem && <div className='toolbar-search'>{searchItem}</div>}
      </Toolbar>
    </AppBar>
  );
};

const mapStateToProps = (state) => ({
  user: userSelector(state)
});

const mapDispatchToProps = {
  logout
};

export default connect(mapStateToProps, mapDispatchToProps)(QDeckToolBar);
